import { useRouter } from 'next/router'
import { useEffect, useState, type FC } from 'react'

import { Text } from '@wartek-id/text-v1'
import { Input, InputGroup, InputLeftAddon } from '@wartek-id/input'
import { Icon } from '@wartek-id/icon-v2'
import { Shimmer } from '@wartek-id/shimmer'
import { Button } from '@wartek-id/button-v1'
import { Pagination } from '@wartek-id/pagination'
import { Link } from '@wartek-id/link'

import useTabKsGuru from 'app/KinerjaShared/hooks/useTabKsGuru'
import { Header } from 'app/KinerjaShared/components/Header'
import FilterSekolah from 'app/KinerjaPSPage/components/FilterSekolah'
import { useApiGetSchoolList } from 'api/kinerja/kepala-sekolah/pengawas-sekolah/pelaksanaan/school'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
  TableShimmer,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import { formatDate, isDateValid } from 'utils/datetime'
import BadgePerilakuKerja from 'app/KinerjaShared/components/BadgePerilakuKerja'
import BadgeObservationStatus from 'app/KinerjaShared/components/BadgeObservationStatus'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import TableRowStates from 'app/KinerjaShared/components/TableRowStates'
import {
  PENGELOLAAN_KINERJA_PS_PATH,
  PENGELOLAAN_KINERJA_KS_PENILAI_PATH,
} from 'app/KinerjaShared/constants/path'
import DialogNoLongerDirectReport from 'app/KinerjaPSPage/components/DialogNoLongerDirectReport'
import { useKinerjaPsTrackEvent } from 'app/KinerjaPSPage/hooks/useKinerjaPsTrackEvent'
import DialogUnconfirmedDirectReport from 'app/KinerjaPSPage/components/DialogUnconfirmedDirectReport'

import type {
  PelaksanaanState,
  PelaksanaanStateFilter,
  PengawasSchool,
} from 'types/kinerja-ks-pelaksanaan'

interface DaftarSekolahProps {}

const RenderCell: FC<{ school: PengawasSchool; value: React.ReactElement }> = ({
  school,
  value,
}) => {
  // If the observation status is not 'PREPARATION', return the value.
  // And if the value is not available, return null.
  if (school?.observationStatus !== 'PREPARATION') return value || null

  // Otherwise, return '-'
  return <>-</>
}

const DaftarSekolah: FC<DaftarSekolahProps> = ({}) => {
  const router = useRouter()
  const [page, setPage] = useState<number>(1)

  const [activeFilter, setCurrentActiveFilter] = useState<
    Array<PelaksanaanStateFilter>
  >(['ALL'])
  const [activeSearch, setCurrentActiveSearch] = useState<string>('')
  const [preSearchText, setPreSearchText] = useState<string>('')
  const [selectedUser, setSelectedUser] = useState<PengawasSchool>(null)

  const { isLoading: isLoadingPengawasProfile, profilePs: pengawasProfile } =
    useRoleContext()

  const { trackOpenDaftarKs, trackClickNilaiOnDaftarKs } =
    useKinerjaPsTrackEvent()

  // Tabs
  const { render: tabs, activeTabName } = useTabKsGuru({
    onChange: () => {
      resetPage()
    },
  })

  const {
    data: { data: schools, meta: metas } = {},
    isLoading: isLoadingSchool,
    isSuccess,
    isError,
    refetch,
  } = useApiGetSchoolList(
    {
      page,
      pageSize: 10,
      name: activeSearch,
      observationStatus: activeFilter?.filter(
        (f) => f !== 'ALL'
      ) as Array<PelaksanaanState>,
      role: activeTabName,
    },
    {
      enabled: !!pengawasProfile,
    }
  )

  const isLoading = isLoadingPengawasProfile || isLoadingSchool

  const isSchoolError = !isLoading && isError
  const hasSchoolData = !isSchoolError && isSuccess && schools?.length > 0
  const hasNoSchoolData =
    !isSchoolError && ((isSuccess && schools?.length === 0) || isError)

  const resetPage = () => {
    setPage(1)
  }

  const getCTAUrl = (school: PengawasSchool) => {
    if (activeTabName === 'GURU')
      return `${PENGELOLAAN_KINERJA_KS_PENILAI_PATH}/penilaian-kinerja/${school?.npsn}/${school?.ptkId}`
    return `${PENGELOLAAN_KINERJA_PS_PATH}/penilaian/${school?.npsn}`
  }

  useEffect(() => {
    if (!!isSuccess) trackOpenDaftarKs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess])

  return (
    <div>
      <Header
        title="Pemantauan Kinerja"
        className="mb-9 print:hidden"
        onBack={() => {
          router.back()
        }}
      />

      <div className="mt-7 bg-white border-gray-30 border rounded-lg">
        <div className="p-6 space-y-6">
          <div className="flex flex-row justify-between items-center">
            <div className="space-y-1 w-8/12">
              <Text variant="heading-md">
                Lakukan Pemantauan serta cek status Praktik Kinerja dan Perilaku
                Kerja
              </Text>
            </div>
            <div className="w-3/12">
              {isLoading ? (
                <Shimmer width={308} height={44} />
              ) : (
                <InputGroup>
                  <InputLeftAddon>
                    <Icon>search</Icon>
                  </InputLeftAddon>
                  <Input
                    disabled={isLoading}
                    placeholder="Cari"
                    value={preSearchText}
                    onKeyDown={(event) => {
                      if (event.key == 'Enter') {
                        setCurrentActiveSearch(preSearchText)

                        // Reset page number
                        resetPage()
                      }
                    }}
                    onChange={(event) => {
                      const stripText = event.target.value.replace(
                        /[^a-z0-9 ]/gi,
                        ''
                      )
                      setPreSearchText(stripText)
                    }}
                  />
                </InputGroup>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        {tabs}

        {/* Filters */}
        <FilterSekolah
          className="mt-4"
          isLoading={isLoading}
          counts={metas?.counts || null}
          activeFilter={activeFilter}
          onChange={(selected) => {
            setCurrentActiveFilter(selected)

            // Reset page number
            resetPage()
          }}
        />

        {/* Table */}
        <TablePelaksanaanKinerja>
          <TableHeader>
            <TableHeaderItem className="w-2/12">Nama</TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">Jabatan</TableHeaderItem>
            <TableHeaderItem className="w-2/12 pl-0">
              Satuan Pendidikan
            </TableHeaderItem>
            <TableHeaderItem className="pl-0">
              Jadwal Observasi Praktik Kinerja
            </TableHeaderItem>
            <TableHeaderItem className="pl-0">
              Status Praktik Kinerja
            </TableHeaderItem>
            <TableHeaderItem className="pl-0">
              Status Perilaku Kerja
            </TableHeaderItem>
            <TableHeaderItem className="w-1/12 pl-0" />
            <TableHeaderItem className="pl-0" />
          </TableHeader>
          <TableBody>
            {/* If loading */}
            {isLoadingSchool && <TableShimmer columns={7} />}

            {/* If error or no data returned */}
            {(isSchoolError || hasNoSchoolData) && (
              <TableRowStates
                type={isSchoolError ? 'ERROR' : 'EMPTY'}
                onClick={() => {
                  // window.location.reload()
                  refetch()
                }}
              />
            )}

            {hasSchoolData &&
              schools.map((school, idx) => {
                const isSubordinate =
                  school?.confirmedSubordinate === 'CONFIRMED'
                const alreadyDone =
                  school?.perilakuKerjaStatus === 'Sudah dinilai' &&
                  school?.observationStatus === 'COMPLETED'

                return (
                  <TableRow key={`ks_${school.npsn}_${idx}`}>
                    <TableRowItem className="align-middle">
                      <Text
                        variant="body"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {school?.name || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="body"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {school?.role || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <Text
                        variant="body"
                        color="default"
                        className="overflow-wrap-anywhere"
                      >
                        {school?.schoolName || ''}
                      </Text>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      {isSubordinate ? (
                        <RenderCell
                          school={school}
                          value={
                            <>
                              {isDateValid(school.observationDate)
                                ? formatDate(
                                    school?.observationDate,
                                    'EEEE, dd MMMM yyyy'
                                  )
                                : '-'}
                            </>
                          }
                        />
                      ) : (
                        <>
                          {/* If kepala sekolah confirmed that the user is not their superior */}
                          {school.confirmedSubordinate === 'REJECTED' && (
                            <Text variant="body-sm" color="disabled">
                              Akses dihapus
                            </Text>
                          )}

                          {/* If kepala sekolah haven't confirm */}
                          {school.confirmedSubordinate === 'UNCONFIRMED' && (
                            <Text variant="body-sm" color="critical">
                              Kepala Sekolah belum konfirmasi data atasan
                            </Text>
                          )}
                        </>
                      )}
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      {isSubordinate && (
                        <RenderCell
                          school={school}
                          value={
                            <BadgeObservationStatus
                              status={school?.observationStatus || null}
                            />
                          }
                        />
                      )}
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      {isSubordinate && (
                        <RenderCell
                          school={school}
                          value={
                            <BadgePerilakuKerja
                              status={school?.perilakuKerjaStatus || null}
                              useNewMapping
                            />
                          }
                        />
                      )}
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <div className="flex flex-row items-center space-x-6 pl-0">
                        <Button
                          size="sm"
                          disabled={
                            (school.observationDate === 'Belum ada jadwal' &&
                              !alreadyDone) ||
                            school.observationStatus === 'PREPARATION' ||
                            school.confirmedSubordinate === 'REJECTED' ||
                            school.confirmedSubordinate === 'UNCONFIRMED'
                          }
                          color={alreadyDone ? 'white' : 'black'}
                          onClick={() => {
                            trackClickNilaiOnDaftarKs()
                            router.push(getCTAUrl(school))
                          }}
                        >
                          {alreadyDone ? 'Cek' : 'Lakukan'}
                        </Button>
                      </div>
                    </TableRowItem>
                    <TableRowItem className="align-middle pl-0">
                      <div className="flex flex-row items-center space-x-2 pl-0">
                        {isSubordinate && alreadyDone && (
                          <Icon color="success">check_circle_outline</Icon>
                        )}

                        {!isSubordinate && (
                          <>
                            <Link
                              as="button"
                              onClick={() => setSelectedUser(school)}
                              tabIndex={0}
                              underline
                              variant="primary"
                            >
                              Info
                            </Link>
                          </>
                        )}
                      </div>
                    </TableRowItem>
                  </TableRow>
                )
              })}
          </TableBody>
        </TablePelaksanaanKinerja>
        {hasSchoolData && (
          <Pagination
            totalCount={metas?.total}
            pageSize={metas?.pageSize}
            currentPage={metas?.page}
            showTotal={(totalCount, startRange, endRange) => {
              if (isLoadingSchool) return 'Memuat data...'

              return !!totalCount
                ? `Menampilkan ${startRange} – ${endRange} dari ${totalCount} data`
                : 'Tidak ada data'
            }}
            onPageChange={(page) => {
              setPage(page)
            }}
          />
        )}
      </div>

      {/* Dialog: ... sudah konfirmasi bahwa Anda tidak lagi menjadi atasannya */}
      <DialogNoLongerDirectReport
        isOpen={
          !!selectedUser && selectedUser.confirmedSubordinate === 'REJECTED'
        }
        name={selectedUser?.name}
        onClose={() => setSelectedUser(null)}
        onClick={() => setSelectedUser(null)}
      />

      {/* Dialog: Kabari Kepala Sekolah untuk konfirmasi data atasan */}
      <DialogUnconfirmedDirectReport
        isOpen={
          !!selectedUser && selectedUser.confirmedSubordinate === 'UNCONFIRMED'
        }
        onClose={() => setSelectedUser(null)}
        onClick={() => setSelectedUser(null)}
      />
    </div>
  )
}
export default DaftarSekolah
